'use client'

/**
 * Pre-redirect notice for the Meta Ads OAuth path.
 *
 * Meta's consent flow includes a "choose the business accounts you want Soku to
 * access" screen. It appears because we request `business_management`, which is
 * what lets account discovery walk each Business Manager and surface every ad
 * account under it. Customers without a Business Manager read that screen as a
 * hard requirement and stop there ("BM 可以不用必选?", 2026-08-14).
 *
 * It is not a hard requirement. Discovery has a second, independent pass over
 * `/me/adaccounts` (`packages/services/credential_context/discovery.py`,
 * "Step B") that needs no Business Manager at all: it surfaces ad accounts
 * assigned directly to the user, plus guest-access accounts. So a user with no
 * Business Manager can complete the connection — as long as the ad account is
 * assigned to their own Meta account.
 *
 * Once the browser leaves for Meta we can no longer say any of this, so it has
 * to land here.
 */

import { Building2, KeyRound, UserCheck } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { OAuthPreflightDialog } from '@/components/oauth-preflight-dialog'

export interface MetaOAuthPreflightDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Runs the actual OAuth start once the user acknowledges. */
  onConfirm: () => void
}

export function MetaOAuthPreflightDialog({
  open,
  onOpenChange,
  onConfirm,
}: MetaOAuthPreflightDialogProps) {
  const t = useTranslations('SettingsPages.integrations.metaOAuthPreflight')

  return (
    <OAuthPreflightDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      title={t('title')}
      description={t('description')}
      points={[
        {
          icon: KeyRound,
          title: t('permissionsTitle'),
          body: t('permissionsBody'),
        },
        {
          icon: Building2,
          title: t('businessTitle'),
          body: t('businessBody'),
        },
        {
          icon: UserCheck,
          title: t('noBusinessTitle'),
          body: t('noBusinessBody'),
        },
      ]}
      cancelLabel={t('cancel')}
      confirmLabel={t('confirm')}
    />
  )
}
